import { Eye, EyeCrossed } from '@atom-learning/icons'
import * as React from 'react'
import { useState } from 'react'

import { ActionIcon } from '~/components/action-icon/ActionIcon'
import { Icon } from '~/components/icon/Icon'
import type { InputProps } from '~/components/input/Input'
import { Input } from '~/components/input/Input'
import { cn } from '~/styled'
import { getFieldIconSize } from '~/utilities/style/get-icon-size'

type PasswordInputProps = Omit<InputProps, 'type'> & {
  hidePasswordText?: string
  showPasswordText?: string
}

export const PasswordInput = React.forwardRef<
  HTMLInputElement,
  PasswordInputProps
>(
  (
    {
      className,
      hidePasswordText = 'Hide password',
      showPasswordText = 'Show password',
      size = 'md',
      ...restProps
    },
    ref
  ) => {
    const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false)
    const togglePasswordVisibility = () =>
      setIsPasswordVisible((currentState) => !currentState)

    const iconSize = React.useMemo(() => getFieldIconSize(size), [size])

    return (
      <div className={cn('relative', className)}>
        <Input
          {...restProps}
          size={size}
          type={isPasswordVisible ? 'text' : 'password'}
          ref={ref}
          className="pr-6"
        />
        <ActionIcon
          appearance="simple"
          theme="neutral"
          label={isPasswordVisible ? hidePasswordText : showPasswordText}
          onClick={togglePasswordVisibility}
          onMouseDown={(e) => e.preventDefault()} // prevent focus being lost from password input
          size={iconSize}
          className={cn(
            size === 'lg' ? 'bottom-1' : 'bottom-0',
            'absolute',
            'right-0'
          )}
        >
          <Icon is={isPasswordVisible ? Eye : EyeCrossed} />
        </ActionIcon>
      </div>
    )
  }
)

PasswordInput.displayName = 'PasswordInput'
